import type { LucideIcon } from "lucide-react";
import {
  AlarmClockIcon,
  ArrowRightLeftIcon,
  AudioLinesIcon,
  BellPlusIcon,
  BellRingIcon,
  BotIcon,
  BriefcaseIcon,
  CalendarCheckIcon,
  CalendarClockIcon,
  CalendarRangeIcon,
  BellOffIcon,
  CheckIcon,
  CircleCheckIcon,
  ClipboardCheckIcon,
  CircleDotIcon,
  CircleHelpIcon,
  CirclePauseIcon,
  CirclePlayIcon,
  CircleXIcon,
  ClockIcon,
  CogIcon,
  FlameIcon,
  HandIcon,
  HourglassIcon,
  InboxIcon,
  LoaderIcon,
  MailIcon,
  MessageCircleIcon,
  MessageSquareDashedIcon,
  MessageSquareIcon,
  MessagesSquareIcon,
  MicIcon,
  MinusIcon,
  MoonIcon,
  NewspaperIcon,
  PhoneIcon,
  ReplyIcon,
  ScanSearchIcon,
  SearchCheckIcon,
  SendIcon,
  SirenIcon,
  SparklesIcon,
  SunriseIcon,
  SunsetIcon,
  TriangleAlertIcon,
  UserIcon,
} from "lucide-react";

import type {
  AssistantMessage,
  AssistantMessageDelivery,
  AssistantOverviewThread,
  AssistantProactiveAsk,
  AssistantProactiveItem,
} from "@/types";

/**
 * One vocabulary for every chip on the Assistant page, so the same state
 * always has the same label, colour and icon, and hovering explains it.
 */

export type Tone =
  | "slate"
  | "green"
  | "emerald"
  | "amber"
  | "orange"
  | "red"
  | "blue"
  | "sky"
  | "indigo"
  | "violet"
  | "teal"
  | "pink";

export interface ChipDef {
  label: string;
  tone: Tone;
  Icon: LucideIcon;
  // Shown on hover and in the legend.
  hint: string;
}

const def = (
  label: string,
  tone: Tone,
  Icon: LucideIcon,
  hint: string,
): ChipDef => ({ label, tone, Icon, hint });

// --- Messages: who sent it and what kind it is.
export const MESSAGE_KIND = {
  staffer_text: def("Staffer · chat", "blue", MessageSquareIcon, "The Staffer typed this."),
  staffer_voice: def("Staffer · voice", "sky", MicIcon, "The Staffer said this on a voice call."),
  reply: def("Phoebe · reply", "slate", BotIcon, "Phoebe answering the Staffer."),
  voice_reply: def("Phoebe · voice", "sky", AudioLinesIcon, "Phoebe speaking on a voice call."),
  nudge: def("Nudge", "amber", BellRingIcon, "First heads-up about a customer email Laborup hasn't answered."),
  renudge: def("Renudge", "orange", BellPlusIcon, "Second and last reminder: the email is still unanswered."),
  digest: def("Digest", "violet", InboxIcon, "The daily 4pm roundup of emails that are probably handled."),
  reminder: def("Reminder", "teal", AlarmClockIcon, "A reminder the Staffer asked for or Phoebe set."),
  plan: def("Plan", "emerald", CalendarRangeIcon, "A day or week plan the Staffer asked to have sent once it was built."),
  quiet_customer: def("Quiet customers", "sky", HourglassIcon, "The daily 9am roundup of customers the Staffer is waiting on who have gone quiet."),
  post_interview: def("Post-interview check-in", "teal", ClipboardCheckIcon, "Asks the Staffer how an interview that just ended went."),
  candidate_news: def("Candidate news", "indigo", NewspaperIcon, "News about one of the Staffer's candidates (a stage move, a reply, an interview)."),
  daily_briefing: def("Briefing", "amber", SunriseIcon, "A scheduled briefing of what needs the Staffer's attention."),
  briefing: def("Morning briefing", "amber", SunriseIcon, "The morning briefing of what needs the Staffer's attention today."),
  wrapup: def("4pm wrap-up", "violet", SunsetIcon, "The 4pm wrap-up of what is still open today."),
  push: def("Push", "violet", SendIcon, "Phoebe sent this on her own, not in reply to the Staffer."),
} satisfies Record<string, ChipDef>;

// Triggers on Phoebe's answers in the conversation; every other trigger is a
// push she sent on her own.
const CHAT_TRIGGERS = new Set(["reply", "voice"]);

/** An assistant message Phoebe sent on her own, not a chat reply. */
export const isPushMessage = (m: AssistantMessage): boolean => {
  const trigger = m.metadata?.trigger;
  return m.role === "ASSISTANT" && !!trigger && !CHAT_TRIGGERS.has(trigger);
};

/** Pushes Phoebe sent in a thread, over the overview's window. */
export const pushCount = ({ proactive: p }: AssistantOverviewThread): number =>
  p.nudge +
  p.renudge +
  p.digest +
  p.reminder +
  p.quietRoundup +
  p.plan +
  p.postInterview +
  p.candidateNews +
  p.briefing +
  p.wrapup;

/** Chat messages in a thread: the Staffer's turns and Phoebe's replies. */
export const chatCount = (t: AssistantOverviewThread): number =>
  t.chatTurns + t.voiceTurns + t.assistantReplies;

// What sent a push, by trigger.
const PUSH_TRIGGER: Record<string, ChipDef> = {
  unanswered_email: def("Unanswered email", "amber", MailIcon, "A customer email Laborup hasn't answered."),
  quiet_customer: MESSAGE_KIND.quiet_customer,
  post_interview: MESSAGE_KIND.post_interview,
  candidate_news: MESSAGE_KIND.candidate_news,
  daily_briefing: MESSAGE_KIND.daily_briefing,
  reminder: MESSAGE_KIND.reminder,
  plan: MESSAGE_KIND.plan,
  slack_note_failed: def("Slack note failed", "red", TriangleAlertIcon, "A Slack note failed after Phoebe said it was on its way, so she told the Staffer here."),
};

/** The trigger of a push, falling back to its raw name for new triggers. */
export const pushTrigger = (trigger: string): ChipDef =>
  PUSH_TRIGGER[trigger] ?? { ...MESSAGE_KIND.push, label: trigger };

export const DELIVERY: Record<AssistantMessageDelivery, ChipDef> = {
  nudge: MESSAGE_KIND.nudge,
  renudge: MESSAGE_KIND.renudge,
  digest: MESSAGE_KIND.digest,
  briefing: MESSAGE_KIND.briefing,
  wrapup: MESSAGE_KIND.wrapup,
};

export const messageKind = (m: AssistantMessage): ChipDef => {
  if (m.role === "USER")
    return m.source === "VOICE" ? MESSAGE_KIND.staffer_voice : MESSAGE_KIND.staffer_text;
  const trigger = m.metadata?.trigger;
  const delivery = m.metadata?.delivery;
  if (trigger === "reminder") return MESSAGE_KIND.reminder;
  if (trigger === "plan") return MESSAGE_KIND.plan;
  if (trigger === "quiet_customer") return MESSAGE_KIND.quiet_customer;
  if (trigger === "post_interview") return MESSAGE_KIND.post_interview;
  if (trigger === "candidate_news") return MESSAGE_KIND.candidate_news;
  if (trigger === "daily_briefing")
    return delivery ? DELIVERY[delivery] : MESSAGE_KIND.daily_briefing;
  if (trigger === "unanswered_email") return DELIVERY[delivery ?? "nudge"];
  if (isPushMessage(m)) return pushTrigger(trigger ?? "");
  return m.source === "VOICE" ? MESSAGE_KIND.voice_reply : MESSAGE_KIND.reply;
};

export const MESSAGE_STATUS = {
  PENDING: def("Generating", "amber", LoaderIcon, "Phoebe is still writing this reply."),
  FAILED: def("Failed", "red", TriangleAlertIcon, "This reply errored and the Staffer saw an error."),
} satisfies Record<string, ChipDef>;

export const IMPORTANT = def("Important", "orange", SirenIcon, "Sent on its own, ahead of the usual nudges, because the email needs a reply within hours.");

// --- Why a proactive email message went out when it did.
export const ORIGIN = {
  scan: def("Hourly scan", "slate", ScanSearchIcon, "Sent by the hourly scan of everyone's mailbox."),
  follow_up: def("Follow-up", "indigo", CalendarClockIcon, "A re-check Phoebe scheduled because Laborup promised something for later."),
  held_for_chat: def("Held for chat", "pink", CirclePauseIcon, "The scan held this because the Staffer was chatting, and sent it once they went quiet."),
} satisfies Record<string, ChipDef>;

// --- Surfaced emails.
export const ITEM_STATUS = {
  open: def("Open", "amber", CircleDotIcon, "The customer is still waiting on Laborup."),
  reply: def("Laborup replied", "green", ReplyIcon, "Someone at Laborup answered the email."),
  evidence: def("Handled elsewhere", "emerald", SearchCheckIcon, "Phoebe found it was handled outside the email: an interview, a text, a call, a stage change…"),
  staffer: def("Staffer: already handled", "slate", HandIcon, "The Staffer tapped “Already handled”."),
  snoozed: def("Snoozed", "indigo", BellOffIcon, "The Staffer snoozed it; Phoebe won't bring it up again until then."),
} satisfies Record<string, ChipDef>;

export const itemStatus = (
  item: Pick<AssistantProactiveItem, "status" | "resolvedBy">,
): ChipDef => {
  if (item.status === "open") return ITEM_STATUS.open;
  if (item.status === "dismissed") return ITEM_STATUS.staffer;
  return item.resolvedBy === "reply" ? ITEM_STATUS.reply : ITEM_STATUS.evidence;
};

export const ASK_STATUS: Record<AssistantProactiveAsk["status"], ChipDef> = {
  open: def("Open", "amber", CircleDotIcon, "Not dealt with yet."),
  handled: def("Handled", "green", CircleCheckIcon, "Dealt with; the evidence is next to it."),
  unclear: def("Unclear", "slate", CircleHelpIcon, "Phoebe couldn't tell either way."),
  scheduled: def("Laborup committed", "indigo", CalendarClockIcon, "Laborup promised it for later, so Phoebe holds off until then."),
};

export const RESOLUTION_KIND: Record<string, ChipDef> = {
  interview_booked: def("Interview booked", "green", CalendarCheckIcon, "An interview was scheduled."),
  candidate_texted: def("Candidate texted", "teal", MessageCircleIcon, "A Laborup recruiter texted the candidate."),
  customer_texted: def("Customer texted", "teal", MessagesSquareIcon, "Laborup and the customer texted."),
  customer_called: def("Customer called", "teal", PhoneIcon, "Laborup and the customer spoke on the phone."),
  stage_moved: def("Stage moved", "blue", ArrowRightLeftIcon, "The candidate moved stage in the pipeline."),
  job_opened: def("Job opened", "blue", BriefcaseIcon, "A job was opened for the customer."),
  later_email: def("Later email", "sky", MailIcon, "The same person was emailed later in another thread."),
  laborup_replied: def("Replied by email", "green", ReplyIcon, "Laborup replied to the email."),
  other: def("Other", "slate", CheckIcon, "Handled some other way."),
};

export const URGENCY: Record<string, ChipDef> = {
  deadline_this_week: def("Due this week", "red", FlameIcon, "The customer needs it this week."),
  deadline_next_week: def("Due next week", "orange", ClockIcon, "The customer needs it next week."),
  no_deadline: def("No deadline", "slate", MinusIcon, "No date given."),
};

// --- Schedules.
export const SCHEDULE_ACTION: Record<string, ChipDef> = {
  remind: def("Reminder", "teal", AlarmClockIcon, "Sends the Staffer a reminder."),
  check_signal: def("Re-check email", "indigo", SearchCheckIcon, "Reads one email again; nudges only if it is still unanswered."),
  deliver_held: def("Held for chat", "pink", CirclePauseIcon, "What the hourly scan held while the Staffer was chatting."),
  deliver_plan: def("Send plan", "emerald", CalendarRangeIcon, "Sends a day or week plan once its scheduled build has run."),
};

export const SCHEDULE_SOURCE: Record<string, ChipDef> = {
  STAFFER: def("Staffer asked", "blue", UserIcon, "The Staffer asked for it in chat."),
  AGENT: def("Phoebe set", "violet", SparklesIcon, "Phoebe set it for herself."),
  SYSTEM: def("System", "slate", CogIcon, "Set by the system (e.g. the scan holding a nudge)."),
};

export const SCHEDULE_STATUS: Record<string, ChipDef> = {
  ACTIVE: def("Upcoming", "blue", CirclePlayIcon, "Waiting to run."),
  DONE: def("Done", "green", CircleCheckIcon, "Ran."),
  CANCELED: def("Canceled", "slate", CircleXIcon, "Canceled before it ran."),
  PAUSED: def("Paused", "amber", CirclePauseIcon, "Paused."),
};

export const DEFERRAL_REASON = {
  in_conversation: def("Staffer was chatting", "pink", MessageSquareDashedIcon, "Pushed back until 30 minutes after the Staffer's last message."),
  quiet_hours: def("Quiet hours", "indigo", MoonIcon, "Nothing proactive goes out from 10pm to 6am Central."),
  plan_not_ready: def("Waiting for plan", "emerald", HourglassIcon, "The plan's build had not landed yet; retried every 30 minutes for up to 6 hours."),
} satisfies Record<string, ChipDef>;

// --- Runs.
export const RUN_RESULT = {
  ok: def("OK", "green", CircleCheckIcon, "Finished without errors."),
  failed: def("Failed", "red", CircleXIcon, "Threw an error; see the message."),
} satisfies Record<string, ChipDef>;

// Left border colour for cards, by tone.
const ACCENTS: Record<Tone, string> = {
  slate: "border-l-slate-300",
  green: "border-l-green-400",
  emerald: "border-l-emerald-400",
  amber: "border-l-amber-400",
  orange: "border-l-orange-400",
  red: "border-l-red-400",
  blue: "border-l-blue-400",
  sky: "border-l-sky-400",
  indigo: "border-l-indigo-400",
  violet: "border-l-violet-400",
  teal: "border-l-teal-400",
  pink: "border-l-pink-400",
};

export const accentFor = (tone: Tone) => ACCENTS[tone];
